"use client";
import { Button, Empty, Modal, Select, Space, Spin, Table, TableProps, Tabs, TabsProps, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { getUserCookies } from "@/service/action/user-cookies";
import { requestGetLatestMessage, requestGetMessageAllRead, requestGetMessageDetail, requestGetMessageList } from "@/lib/api/message";

const options = [{
  value: "1",
  label: "近七天内的消息"
}, {
  value: "2",
  label: "近一个月内的消息"
}, {
  value: "3",
  label: "近三个月内的消息"
}, {
  value: "4",
  label: "近半年内的消息"
}, {
  value: "5",
  label: "近一年内的消息"
}, {
  value: "6",
  label: "全部消息"
}];

interface MessageNotify {
  id: string;
  title: string;
  content: string;
  action?: string;
  target_id?: string;
  target_type?: string;
}

interface MessageListItem {
  id: string;
  is_read: string;
  created_at: string;
  notify: MessageNotify;
}

interface LatestMessagePayload {
  newest?: MessageListItem;
  unReadCount?: {
    count: number;
    announce: number;
    remind: number;
    message: number;
  };
}

interface TableRow {
  key: number;
  id: number;
  desc: string;
  title: string;
  time: Date;
  status: "read" | "unread";
}

const tabItems: TabsProps["items"] = [
  { key: "1", label: "全部" },
  { key: "2", label: "最新" },
];

const normalizeMessageList = (raw: unknown): MessageListItem[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as MessageListItem[];
  if (typeof raw === "object" && raw !== null && "list" in raw) {
    const list = (raw as { list?: unknown }).list;
    return Array.isArray(list) ? (list as MessageListItem[]) : [];
  }
  return [raw as MessageListItem];
};

/** 解析 /notify/newest 接口：{ newest, unReadCount } 或 [{ newest, unReadCount }] */
const parseLatestMessageData = (raw: unknown): LatestMessagePayload => {
  if (!raw) return {};
  if (Array.isArray(raw)) return (raw[0] as LatestMessagePayload) ?? {};
  if (typeof raw === "object" && raw !== null && "newest" in raw) {
    return raw as LatestMessagePayload;
  }
  return {};
};

const mapMessageToRow = (item: MessageListItem): TableRow => ({
  key: Number(item.id),
  id: Number(item.id),
  title: item.notify?.title ?? "",
  desc: item.notify?.content ?? "",
  time: new Date(Number(item.created_at) * 1000),
  status: item.is_read === "1" ? "read" : "unread",
});

const normalizeMessageDetail = (raw: unknown): MessageListItem | null => {
  if (!raw || typeof raw !== "object") return null;
  if ("notify" in raw) return raw as MessageListItem;
  if ("title" in raw || "content" in raw) {
    const notify = raw as MessageNotify;
    return {
      id: "",
      is_read: "1",
      created_at: String(Math.floor(Date.now() / 1000)),
      notify,
    };
  }
  return null;
};

const baseColumns: ColumnsType<TableRow> = [{
  key: "title",
  title: "标题",
  dataIndex: "title"
}, {
  key: "desc",
  title: "描述",
  dataIndex: "desc",
  width: 300,
  render: (text: string) => <p className="line-clamp-1">{text}</p>
}, {
  key: "time",
  title: "时间",
  dataIndex: "time",
  render: (text: Date) => dayjs(text).format("YYYY-MM-DD HH:mm:ss")
}, {
  key: "status",
  title: "状态",
  dataIndex: "status",
  render: (text: "read" | "unread") => text === "read" ? <Tag color={"success"}>已读</Tag> :
    <Tag color={"warning"}>未读</Tag>
}];

export default function Content() {
  const [activeKey, setActiveKey] = useState("1");
  const [data, setData] = useState<MessageListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [openViewMsg, setOpenViewMsg] = useState(false);
  const [messageDetail, setMessageDetail] = useState<MessageListItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [markAllReadLoading, setMarkAllReadLoading] = useState(false);

  const tableData = useMemo(() => data.map(mapMessageToRow), [data]);

  const getMessageAction = async (page = currentPage, limit = pageSize) => {
    const userInfo = await getUserCookies();
    if (!userInfo) return;
    setLoading(true);

    if (activeKey === "2") {
      const res = await requestGetLatestMessage({
        "access-token": userInfo.access_token,
      });
      if (res.code === 200) {
        const { newest } = parseLatestMessageData(res.data);
        const list = newest ? [newest] : [];
        // console.log(list,'list000');
        setData(list);
        setTotalCount(list.length);
      }
    } else {
      const res = await requestGetMessageList({
        "access-token": userInfo.access_token,
        page: String(page),
        limit: String(limit),
      });
      if (res.code === 200) {
        const payload = res.data as { list?: MessageListItem[]; pages?: { totalCount?: number } };
        const list = payload.list ?? [];
        // console.log(list,'list111');
        setData(list);
        setTotalCount(Number(payload.pages?.totalCount ?? list.length));
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    getMessageAction();
  }, [currentPage, pageSize, activeKey]);

  const onTabChange = (key: string) => {
    setActiveKey(key);
    setCurrentPage(1);
  };

  const handleMarkAllRead = async () => {
    const userInfo = await getUserCookies();
    if (!userInfo) return;

    setMarkAllReadLoading(true);
    const res = await requestGetMessageAllRead({
      "access-token": userInfo.access_token,
    });
    setMarkAllReadLoading(false);

    if (res.code === 200) {
      message.success("已全部标记为已读");
      getMessageAction();
      return;
    }

    message.error(res.message || "标记失败");
  };

  const handleCloseDetail = () => {
    setOpenViewMsg(false);
    setMessageDetail(null);
  };

  const handleViewMessage = useCallback(async (record: TableRow) => {
    const userInfo = await getUserCookies();
    if (!userInfo) return;

    setOpenViewMsg(true);
    setDetailLoading(true);
    setMessageDetail(null);

    const res = await requestGetMessageDetail({
      "access-token": userInfo.access_token,
      id: String(record.id),
    });

    setDetailLoading(false);

    if (res.code === 200) {
      const detail = normalizeMessageDetail(res.data);
      if (detail) {
        setMessageDetail(detail);
        if (record.status === "unread") {
          getMessageAction();
        }
        return;
      }
    }

    message.error(res.message || "获取消息详情失败");
    handleCloseDetail();
  }, [activeKey, currentPage, pageSize]);

  const columns: TableProps<TableRow>["columns"] = useMemo(() => [
    ...(baseColumns ?? []),
    {
      key: "action",
      title: "操作",
      dataIndex: "action",
      render: (_text: string, record: TableRow) => <Space>
        <Button size="small" type="primary" onClick={() => handleViewMessage(record)}>查看</Button>
        {
          record.status === "unread" && <Button type="primary" ghost size="small">标记已读</Button>
        }
        {/* <Button size="small" type="primary" danger>删除</Button> */}
      </Space>
    },
  ], [handleViewMessage]);

  return <>
    <div className="b-flex gap-x-3">
      <Tabs activeKey={activeKey} onChange={onTabChange} className="!flex-1" items={tabItems} />
      {activeKey === "1" && (
        <Button loading={markAllReadLoading} onClick={handleMarkAllRead}>
          全部标记已读
        </Button>
      )}
      <div className="w-[200px]">
        <Select options={options} defaultValue={"1"} className="!w-full" />
      </div>
    </div>
    {
      !tableData.length && !loading ? <Empty description="暂无消息" className="!py-48" /> :
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={tableData}
          pagination={activeKey === "1" ? {
            current: currentPage,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              setPageSize(newPageSize);
            },
          } : false}
        />
    }
    <Modal
      open={openViewMsg}
      title={messageDetail?.notify?.title ?? "消息详情"}
      onCancel={handleCloseDetail}
      footer={null}
      width={600}
      destroyOnClose
      maskClosable={!detailLoading}
    >
      <Spin spinning={detailLoading}>
        {messageDetail && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                {dayjs(Number(messageDetail.created_at) * 1000).format("YYYY-MM-DD HH:mm:ss")}
              </span>
              <Tag color={messageDetail.is_read === "1" ? "success" : "warning"}>
                {messageDetail.is_read === "1" ? "已读" : "未读"}
              </Tag>
            </div>
            <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {messageDetail.notify?.content}
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  </>;
}
